import { Empty } from 'antd';
import { useMemo } from 'react';
import tableDesign from 'themes/table';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';

const LightTable = ({
	data = [],
	title = '',
	tableSx = {},
	columns = [],
	headerCellSx = {},
	maxHeight = '600px',
	lastRowProps = null,
	tableContainerSx = {},
	emptyMessage = 'No data available',
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const isTablet = useMediaQuery(theme.breakpoints.down('md'));

	const getCellValue = (row, column, rowIndex) => {
		if (column.Cell) {
			return column.Cell({ row: { original: row }, index: rowIndex });
		}

		if (column.accessor) {
			if (typeof column.accessor === 'function') {
				return column.accessor(row);
			}

			return row[column.accessor];
		}

		return null;
	};

	const formatCurrency = (value) => {
		if (typeof value === 'number') {
			return new Intl.NumberFormat('en-US', {
				style: 'decimal',
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			}).format(value);
		}

		return value;
	};

	const isNegativeAmount = (value) => {
		if (typeof value === 'number') {
			return value < 0;
		}

		if (typeof value === 'string') {
			const numericValue = parseFloat(value.replace(/[^\d.-]/g, ''));

			return !isNaN(numericValue) && numericValue < 0;
		}

		return false;
	};

	const isAmountColumn = (column) => {
		if (!column.Header || typeof column.Header !== 'string') {
			return false;
		}

		return column.Header.toLowerCase().includes('amount');
	};

	const getColumnWidths = useMemo(() => {
		return columns.map((column) => {
			const width =
				column.width ||
				(isAmountColumn(column) ? '140px' : column.Header === 'Date' ? '120px' : column.Header === 'Description/Type' ? '200px' : '150px');

			return width;
		});
	}, [columns]);

	return (
		<Box sx={{ width: '100%' }}>
			{title && (
				<Typography
					variant="h6"
					sx={{
						mb: 2,
						fontWeight: 'bold',
						fontSize: isMobile ? '16px' : '18px',
					}}
				>
					{title}
				</Typography>
			)}

			<TableContainer
				sx={{
					width: '100%',
					overflow: 'auto',
					borderRadius: '4px',
					position: 'relative',
					// border: '1px solid #e0e0e0',
					maxHeight: isMobile ? '400px' : maxHeight,
					// boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',

					'&::-webkit-scrollbar': {
						height: '8px',
						width: '8px',
					},

					'&::-webkit-scrollbar-track': {
						backgroundColor: '#f1f1f1',
					},

					'&::-webkit-scrollbar-thumb': {
						backgroundColor: '#c1c1c1',
						borderRadius: '4px',
					},

					'&::-webkit-scrollbar-thumb:hover': {
						backgroundColor: '#a1a1a1',
					},

					...tableContainerSx,
				}}
			>
				<Table
					stickyHeader
					size={isMobile || isTablet ? 'small' : 'medium'}
					sx={{
						tableLayout: 'fixed',
						minWidth: columns.length * 150,

						'& td, th': {
							borderRight: 1,
							borderColor: tableDesign.borderColor.primary,
						},

						'& th': {
							'&:first-of-type': { borderLeft: `1px solid ${tableDesign.background.primary}` },
						},
						'& td': {
							'&:first-of-type': { borderLeft: `1px solid ${tableDesign.borderColor.primary}` },
						},

						...tableSx,
					}}
				>
					<TableHead>
						<TableRow>
							{columns.map((column, index) => (
								<TableCell
									key={`header-${index}`}
									align={column.align || (isAmountColumn(column) ? 'right' : 'left')}
									sx={{
										top: 0,
										zIndex: 2,
										overflow: 'hidden',
										position: 'sticky',
										padding: '8px 16px',
										whiteSpace: 'nowrap',
										textOverflow: 'ellipsis',
										color: tableDesign.color.primary,
										fontSize: tableDesign.headerFontSize.primary,
										backgroundColor: tableDesign.background.primary,
										fontWeight: tableDesign.headerFontWeight.primary,

										width: getColumnWidths[index],
										minWidth: getColumnWidths[index],
										maxWidth: getColumnWidths[index],

										...(column.headerSx || {}),
										...headerCellSx,
									}}
								>
									<Tooltip title={column.Header || ''} arrow placement="top">
										{column.Header}
									</Tooltip>
								</TableCell>
							))}
						</TableRow>
					</TableHead>

					<TableBody
						sx={{
							'& tr:nth-of-type(even)': {
								backgroundColor: `${tableDesign.tableRowBackgroundColor.primary} !important`,
							},
						}}
					>
						{data.length > 0 ? (
							data.map((row, rowIndex) => {
								const isLastRow = rowIndex === data.length - 1 && lastRowProps !== null;

								return (
									<TableRow
										hover={!isLastRow}
										key={`row-${rowIndex}`}
										sx={{
											// borderBottom: '1px solid #b5b5b5',

											// '&:nth-of-type(odd)': {
											// 	backgroundColor: isLastRow ? 'inherit' : '#f9f9f9',
											// },

											// '&:last-child': {
											// 	borderBottom: 'none',
											// },

											// '&:hover': {
											// 	backgroundColor: isLastRow ? undefined : '#f0f7ff',
											// },

											...(isLastRow ? lastRowProps.rowSx || {} : {}),
										}}
									>
										{columns.map((column, colIndex) => {
											let content = getCellValue(row, column, rowIndex);

											if (isAmountColumn(column) && typeof content === 'number') {
												content = formatCurrency(content);
											}

											const isNegative = isAmountColumn(column) && isNegativeAmount(content);

											return (
												<TableCell
													key={`cell-${rowIndex}-${colIndex}`}
													align={column.cellAlign || column.align || (isAmountColumn(column) ? 'right' : 'left')}
													sx={{
														overflow: 'hidden',
														padding: '8px 16px',
														whiteSpace: 'nowrap',
														textOverflow: 'ellipsis',
														borderBottom: '1px solid #E0E0E0',

														width: getColumnWidths[colIndex],
														minWidth: getColumnWidths[colIndex],
														maxWidth: getColumnWidths[colIndex],

														...(column.cellSx || {}),
														...(isLastRow ? lastRowProps.cellSx || {} : {}),
													}}
												>
													<Tooltip
														arrow
														placement="top"
														title={content !== null && content !== undefined ? String(content) : ''}
													>
														{/* <Typography
															noWrap
															variant="body2"
															component="span"
															sx={{
																display: 'block',
																overflow: 'hidden',
																whiteSpace: 'nowrap',
																textOverflow: 'ellipsis',
																fontSize: isMobile ? '12px' : '13px',
																color: isNegative ? 'error.main' : 'inherit',

																...(isLastRow ? lastRowProps.typographySx || {} : {}),
															}}
														> */}
														{content}
														{/* </Typography> */}
													</Tooltip>
												</TableCell>
											);
										})}
									</TableRow>
								);
							})
						) : (
							<TableRow>
								<TableCell colSpan={columns.length}>
									<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
										<Empty description={emptyMessage} />
									</Box>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{isMobile && data.length > 0 && (
				<Typography
					variant="caption"
					sx={{
						mt: 1,
						display: 'block',
						textAlign: 'center',
						color: 'text.secondary',
						animation: 'pulse 2s infinite ease-in-out',

						'@keyframes pulse': {
							'0%': { opacity: 0.6 },
							'50%': { opacity: 1 },
							'100%': { opacity: 0.6 },
						},
					}}
				>
					Swipe horizontally to see more columns →
				</Typography>
			)}
		</Box>
	);
};

export default LightTable;

// import React, { useMemo } from 'react';
// import {
// 	Box,
// 	Table,
// 	TableBody,
// 	TableCell,
// 	TableContainer,
// 	TableHead,
// 	TableRow,
// 	Tooltip,
// 	Typography,
// 	useMediaQuery,
// 	useTheme,
// } from '@mui/material';
// import { Empty } from 'antd';

// /**
//  * Enhanced responsive table component optimized for lease liability data
//  * Shows all data without pagination and maintains horizontal scrolling
//  */
// const LightTable = ({
// 	data = [],
// 	columns = [],
// 	emptyMessage = 'No data available',
// 	tableContainerSx = {},
// 	tableSx = {},
// 	headerCellSx = {},
// 	title = '',
// 	maxHeight = '600px', // Default max height
// 	lastRowProps = null, // Props for the last row
// }) => {
// 	const theme = useTheme();
// 	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
// 	const isTablet = useMediaQuery(theme.breakpoints.down('md'));

// 	// Function to safely get cell value
// 	const getCellValue = (row, column, rowIndex) => {
// 		if (column.Cell) {
// 			return column.Cell({ row: { original: row }, index: rowIndex });
// 		}

// 		if (column.accessor) {
// 			if (typeof column.accessor === 'function') {
// 				return column.accessor(row);
// 			}
// 			return row[column.accessor];
// 		}

// 		return null;
// 	};

// 	// Format currency amounts with proper alignment
// 	const formatCurrency = (value) => {
// 		if (typeof value === 'number') {
// 			return new Intl.NumberFormat('en-US', {
// 				style: 'decimal',
// 				minimumFractionDigits: 2,
// 				maximumFractionDigits: 2,
// 			}).format(value);
// 		}
// 		return value;
// 	};

// 	// Safely determine if a value is negative (for styling)
// 	const isNegativeAmount = (value) => {
// 		if (typeof value === 'number') {
// 			return value < 0;
// 		}
// 		if (typeof value === 'string') {
// 			// Try to extract a number from the string by removing commas and other formatting
// 			const numericValue = parseFloat(value.replace(/[^\d.-]/g, ''));
// 			return !isNaN(numericValue) && numericValue < 0;
// 		}
// 		return false;
// 	};

// 	// Helper to check if column is an amount column
// 	const isAmountColumn = (column) => {
// 		if (!column.Header || typeof column.Header !== 'string') {
// 			return false;
// 		}
// 		return column.Header.toLowerCase().includes('amount');
// 	};

// 	// Calculate column widths (using column.width or a default)
// 	const getColumnWidths = useMemo(() => {
// 		return columns.map((column) => {
// 			const width =
// 				column.width ||
// 				(isAmountColumn(column) ? '140px' : column.Header === 'Date' ? '120px' : column.Header === 'Description/Type' ? '200px' : '150px');
// 			return width;
// 		});
// 	}, [columns]);

// 	return (
// 		<Box sx={{ width: '100%' }}>
// 			{title && (
// 				<Typography
// 					variant="h6"
// 					sx={{
// 						mb: 2,
// 						fontWeight: 'bold',
// 						fontSize: isMobile ? '16px' : '18px',
// 					}}
// 				>
// 					{title}
// 				</Typography>
// 			)}

// 			<TableContainer
// 				sx={{
// 					width: '100%',
// 					position: 'relative',
// 					overflow: 'auto',
// 					borderRadius: '8px',
// 					boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
// 					border: '1px solid #e0e0e0',
// 					maxHeight: isMobile ? '400px' : maxHeight,
// 					'&::-webkit-scrollbar': {
// 						height: '8px',
// 						width: '8px',
// 					},
// 					'&::-webkit-scrollbar-track': {
// 						backgroundColor: '#f1f1f1',
// 					},
// 					'&::-webkit-scrollbar-thumb': {
// 						backgroundColor: '#c1c1c1',
// 						borderRadius: '4px',
// 					},
// 					'&::-webkit-scrollbar-thumb:hover': {
// 						backgroundColor: '#a1a1a1',
// 					},
// 					...tableContainerSx,
// 				}}
// 			>
// 				<Table
// 					stickyHeader
// 					size={isMobile || isTablet ? 'small' : 'medium'}
// 					sx={{
// 						tableLayout: 'fixed',
// 						minWidth: columns.length * 150, // Ensure table is wide enough for all columns
// 						...tableSx,
// 					}}
// 				>
// 					<TableHead>
// 						<TableRow>
// 							{columns.map((column, index) => (
// 								<TableCell
// 									key={`header-${index}`}
// 									align={column.align || (isAmountColumn(column) ? 'right' : 'left')}
// 									sx={{
// 										backgroundColor: '#e1f0ff !important',
// 										fontSize: isMobile ? '12px' : isTablet ? '13px' : '14px',
// 										fontWeight: 'bold',
// 										padding: isMobile ? '6px 8px' : '8px 16px',
// 										whiteSpace: 'nowrap',
// 										overflow: 'hidden',
// 										textOverflow: 'ellipsis',
// 										position: 'sticky',
// 										top: 0,
// 										width: getColumnWidths[index],
// 										minWidth: getColumnWidths[index],
// 										maxWidth: getColumnWidths[index],
// 										zIndex: 2,
// 										...(column.headerSx || {}),
// 										...headerCellSx,
// 									}}
// 								>
// 									<Tooltip title={column.Header || ''} arrow placement="top">
// 										<Typography
// 											noWrap
// 											sx={{
// 												overflow: 'hidden',
// 												textOverflow: 'ellipsis',
// 											}}
// 										>
// 											{column.Header}
// 										</Typography>
// 									</Tooltip>
// 								</TableCell>
// 							))}
// 						</TableRow>
// 					</TableHead>
// 					<TableBody>
// 						{data.length > 0 ? (
// 							data.map((row, rowIndex) => {
// 								// Determine if this is the last row and should use custom props
// 								const isLastRow = rowIndex === data.length - 1 && lastRowProps !== null;

// 								return (
// 									<TableRow
// 										key={`row-${rowIndex}`}
// 										hover={!isLastRow} // Don't apply hover to the last row with custom styling
// 										sx={{
// 											borderBottom: '1px solid #b5b5b5',
// 											'&:nth-of-type(odd)': {
// 												backgroundColor: isLastRow ? 'inherit' : '#f9f9f9',
// 											},
// 											'&:last-child': {
// 												borderBottom: 'none',
// 											},
// 											'&:hover': {
// 												backgroundColor: isLastRow ? undefined : '#f0f7ff',
// 											},
// 											// Apply custom styling for the last row if provided
// 											...(isLastRow ? lastRowProps.rowSx || {} : {}),
// 										}}
// 									>
// 										{columns.map((column, colIndex) => {
// 											// Get cell content
// 											let content = getCellValue(row, column, rowIndex);

// 											// Format amounts
// 											if (isAmountColumn(column) && typeof content === 'number') {
// 												content = formatCurrency(content);
// 											}

// 											// Determine if this is a negative amount for styling
// 											const isNegative = isAmountColumn(column) && isNegativeAmount(content);

// 											return (
// 												<TableCell
// 													key={`cell-${rowIndex}-${colIndex}`}
// 													align={column.cellAlign || column.align || (isAmountColumn(column) ? 'right' : 'left')}
// 													sx={{
// 														padding: isMobile ? '6px 8px' : '8px 16px',
// 														fontSize: isMobile ? '12px' : isTablet ? '13px' : '14px',
// 														overflow: 'hidden',
// 														textOverflow: 'ellipsis',
// 														whiteSpace: 'nowrap',
// 														width: getColumnWidths[colIndex],
// 														minWidth: getColumnWidths[colIndex],
// 														maxWidth: getColumnWidths[colIndex],
// 														...(column.cellSx || {}),
// 														// Apply custom cell styling for the last row if provided
// 														...(isLastRow ? lastRowProps.cellSx || {} : {}),
// 													}}
// 												>
// 													<Tooltip
// 														title={content !== null && content !== undefined ? String(content) : ''}
// 														arrow
// 														placement="top"
// 													>
// 														<Typography
// 															variant="body2"
// 															component="span"
// 															noWrap
// 															sx={{
// 																whiteSpace: 'nowrap',
// 																overflow: 'hidden',
// 																textOverflow: 'ellipsis',
// 																display: 'block',
// 																color: isNegative ? 'error.main' : 'inherit',
// 																fontSize: isMobile ? '12px' : '13px',
// 																// Apply custom typography styling for last row if provided
// 																...(isLastRow ? lastRowProps.typographySx || {} : {}),
// 															}}
// 														>
// 															{content}
// 														</Typography>
// 													</Tooltip>
// 												</TableCell>
// 											);
// 										})}
// 									</TableRow>
// 								);
// 							})
// 						) : (
// 							<TableRow>
// 								<TableCell colSpan={columns.length}>
// 									<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
// 										<Empty description={emptyMessage} />
// 									</Box>
// 								</TableCell>
// 							</TableRow>
// 						)}
// 					</TableBody>
// 				</Table>
// 			</TableContainer>

// 			{/* Horizontal Scroll Indicator */}
// 			{isMobile && data.length > 0 && (
// 				<Typography
// 					variant="caption"
// 					sx={{
// 						display: 'block',
// 						textAlign: 'center',
// 						mt: 1,
// 						color: 'text.secondary',
// 						animation: 'pulse 2s infinite ease-in-out',
// 						'@keyframes pulse': {
// 							'0%': { opacity: 0.6 },
// 							'50%': { opacity: 1 },
// 							'100%': { opacity: 0.6 },
// 						},
// 					}}
// 				>
// 					Swipe horizontally to see more columns →
// 				</Typography>
// 			)}
// 		</Box>
// 	);
// };

// export default LightTable;
